// ── BCH Rooms — audio transport abstraction ───────────────────────
//
// The room UI talks to an *interface*, never to WebRTC directly. That is
// the whole point of this file: today the only implementation is a
// peer-to-peer mesh (no server, $0), but rooms outgrow a mesh once there
// are many listeners. When that happens an SFU-backed implementation
// (LiveKit et al.) can be dropped in behind the same interface without
// touching a single component.
//
// ── Why a mesh has a ceiling ─────────────────────────────────────
//
// In a full mesh every participant sends audio to every other, so cost
// grows with N². The fix used here is the speaker/listener split that
// real audio spaces use:
//
//   • SPEAKERS publish audio and subscribe to each other (small mesh).
//   • LISTENERS publish nothing and only subscribe.
//
// That makes the speaker mesh tiny (a handful of people) while listeners
// are cheap to add on the receiving side. Each speaker still uploads one
// stream per listener, so upload bandwidth — not the speaker count — is
// what eventually caps a P2P room. Expect comfortable rooms in the
// ~10–20 range; past that you want an SFU.

import { getIceServers } from './rooms';

/** How a participant is taking part. */
export type RoomRole = 'speaker' | 'listener';

/**
 * Connection health for a remote peer, so the UI can tell the difference
 * between "still connecting" and "will never connect". The second case is
 * common without a TURN server and must be surfaced, not hidden behind a
 * spinner — see `hasTurn()` in lib/rooms.ts.
 */
export type PeerStatus = 'connecting' | 'connected' | 'failed';

export interface RoomParticipant {
  pubkey: string;
  role: RoomRole;
  /** Peer connection is established and flowing. */
  connected: boolean;
  /** Finer-grained than `connected`: distinguishes failure from progress. */
  status: PeerStatus;
  /** Participant has muted their own mic. */
  muted: boolean;
  /** Currently talking (from audio-level analysis). */
  speaking: boolean;
  /** Raised a hand to ask to speak. */
  handRaised: boolean;
}

/**
 * Signalling payloads exchanged over Nostr (kind 25050).
 *
 * `state` carries mute / hand-raise. WebRTC gives no reliable way to see
 * whether a remote peer muted (a disabled track still sends silence), so
 * it has to be announced explicitly.
 */
export type SignalPayload =
  | { type: 'offer'; sdp: string; from: string; role: RoomRole }
  | { type: 'answer'; sdp: string; from: string; role: RoomRole }
  | { type: 'ice'; candidate: RTCIceCandidateInit; from: string }
  | { type: 'join'; from: string; role: RoomRole }
  | { type: 'leave'; from: string }
  | { type: 'state'; from: string; role: RoomRole; muted: boolean; handRaised: boolean };

/**
 * Everything the room UI needs from an audio transport. Implemented today
 * by `createMeshTransport`; an SFU implementation would satisfy the same
 * contract.
 */
export interface RoomAudioTransport {
  /** Join the room. Requests the mic only when joining as a speaker. */
  join(role: RoomRole): Promise<void>;
  leave(): void;
  /**
   * Re-broadcast our presence to the room.
   *
   * Kind 25050 is *ephemeral*, so relays do not store it: a `join` sent
   * before our subscription is open is lost forever rather than being
   * replayed by `since`. Callers therefore announce again once the
   * subscription is definitely live.
   */
  announce(): Promise<void>;
  /**
   * Retry playback on every remote stream. Must be called from a user
   * gesture (a tap) to satisfy mobile autoplay policies.
   */
  resumeAudio(): Promise<void>;
  /** Mute/unmute the local mic. No-op for listeners. */
  setMuted(muted: boolean): void;
  /** Raise/lower the local hand, announced to peers. */
  setHandRaised(raised: boolean): void;
  /** Promote/demote self between speaker and listener mid-session. */
  setRole(role: RoomRole): Promise<void>;
  /** Local mic stream, for speaking detection. Null for listeners. */
  getLocalStream(): MediaStream | null;
  /** Feed an inbound signalling payload (from the Nostr subscription). */
  handleSignal(payload: SignalPayload): Promise<void>;
  /** Current participants, including self. */
  getParticipants(): RoomParticipant[];
  /** Mark a peer as gone (e.g. their presence event aged out). */
  removePeer(pubkey: string): void;
  destroy(): void;
}

export interface MeshTransportOptions {
  selfPubkey: string;
  /** Publish a signalling payload to a specific peer over Nostr. */
  sendSignal: (toPubkey: string, payload: SignalPayload) => Promise<void>;
  /** Called whenever the participant list changes, so the UI re-renders. */
  onParticipantsChanged: (participants: RoomParticipant[]) => void;
  /** Called when a remote stream arrives, so it can be played. */
  onRemoteStream?: (pubkey: string, stream: MediaStream) => void;
  onError?: (message: string) => void;
  /**
   * Playback was blocked by the browser's autoplay policy. Mobile Safari
   * and Chrome block audio that isn't tied to a user gesture, and the
   * failure is silent — the user simply hears nothing. The UI should show
   * a "tap to enable sound" affordance and then call `resumeAudio()`.
   */
  onAudioBlocked?: () => void;
}

/** Mic constraints tuned for speech: mono, and let the browser clean it up. */
const MIC_CONSTRAINTS: MediaStreamConstraints = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    channelCount: 1,
  },
  video: false,
};

interface PeerEntry {
  pc: RTCPeerConnection;
  audioEl: HTMLAudioElement;
  role: RoomRole;
  muted: boolean;
  handRaised: boolean;
  speaking: boolean;
  /** Tears down the speaking analyser for this peer's inbound stream. */
  stopSpeakingWatch?: () => void;
}

/**
 * Peer-to-peer mesh transport.
 *
 * Speakers mesh with each other and with listeners; listeners never
 * publish. Signalling rides on Nostr, so there is no server at all.
 */
export function createMeshTransport(opts: MeshTransportOptions): RoomAudioTransport {
  const {
    selfPubkey, sendSignal, onParticipantsChanged, onRemoteStream, onError,
    onAudioBlocked,
  } = opts;

  const peers = new Map<string, PeerEntry>();
  let localStream: MediaStream | null = null;
  let selfRole: RoomRole = 'listener';
  let selfMuted = false;
  let selfHandRaised = false;
  let selfSpeaking = false;
  let joined = false;
  let stopLocalSpeakingWatch: (() => void) | null = null;

  const emit = () => onParticipantsChanged(snapshot());

  function snapshot(): RoomParticipant[] {
    const list: RoomParticipant[] = [{
      pubkey: selfPubkey,
      role: selfRole,
      connected: joined,
      status: joined ? 'connected' : 'connecting',
      muted: selfMuted,
      speaking: selfSpeaking,
      handRaised: selfHandRaised,
    }];
    for (const [pubkey, p] of peers) {
      const state = p.pc.iceConnectionState;
      const connected = state === 'connected' || state === 'completed';
      // 'disconnected' is transient (ICE may recover), so it stays
      // 'connecting'; only 'failed'/'closed' are terminal.
      const status: PeerStatus =
        connected ? 'connected'
          : state === 'failed' || state === 'closed' ? 'failed'
            : 'connecting';
      list.push({
        pubkey,
        role: p.role,
        connected,
        status,
        muted: p.muted,
        // A muted speaker must never render as talking, whatever the
        // analyser last saw before the track was disabled.
        speaking: p.speaking && !p.muted,
        handRaised: p.handRaised,
      });
    }
    return list;
  }

  /** Announce our mute / hand state to every peer. */
  function broadcastState() {
    for (const pubkey of peers.keys()) {
      sendSignal(pubkey, {
        type: 'state',
        from: selfPubkey,
        role: selfRole,
        muted: selfMuted,
        handRaised: selfHandRaised,
      }).catch(() => {});
    }
  }

  async function ensureMic(): Promise<MediaStream | null> {
    if (localStream) return localStream;
    try {
      localStream = await navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
      return localStream;
    } catch (err) {
      const name = err instanceof Error ? err.name : '';
      onError?.(
        name === 'NotAllowedError'
          ? 'Microphone permission denied. Allow mic access to speak.'
          : name === 'NotFoundError'
            ? 'No microphone found on this device.'
            : 'Could not access the microphone.',
      );
      return null;
    }
  }

  function releaseMic() {
    stopLocalSpeakingWatch?.();
    stopLocalSpeakingWatch = null;
    selfSpeaking = false;
    localStream?.getTracks().forEach(t => t.stop());
    localStream = null;
  }

  /**
   * Start the local speaking analyser. The transport owns this so every
   * consumer gets self-speaking for free and there is exactly one
   * AudioContext per session.
   */
  function startLocalSpeakingWatch() {
    if (!localStream || stopLocalSpeakingWatch) return;
    stopLocalSpeakingWatch = watchSpeaking(localStream, speaking => {
      // While muted we are not speaking as far as anyone else is concerned.
      const next = speaking && !selfMuted;
      if (next === selfSpeaking) return;
      selfSpeaking = next;
      emit();
    });
  }

  /** Create (or fetch) the peer connection for a pubkey. */
  function getPeer(pubkey: string, role: RoomRole): PeerEntry {
    const existing = peers.get(pubkey);
    if (existing) return existing;

    // Read ICE servers per connection so a TURN server configured mid-
    // session is picked up by the next peer without a reload.
    const pc = new RTCPeerConnection({ iceServers: getIceServers() });
    const audioEl = document.createElement('audio');
    audioEl.autoplay = true;
    // iOS treats an audio element without `playsinline` as a candidate for
    // fullscreen/native playback and will refuse to start it inline, which
    // means silent rooms on iPhone. It must be set as an attribute.
    audioEl.setAttribute('playsinline', '');
    audioEl.setAttribute('autoplay', '');
    // Remote audio must never be muted by default or nobody is heard.
    audioEl.muted = false;
    audioEl.volume = 1;
    // Keep the element out of the layout; it exists purely to play audio.
    audioEl.style.display = 'none';
    document.body.appendChild(audioEl);

    const entry: PeerEntry = { pc, audioEl, role, muted: false, handRaised: false, speaking: false };

    pc.onicecandidate = e => {
      if (e.candidate) {
        sendSignal(pubkey, { type: 'ice', candidate: e.candidate.toJSON(), from: selfPubkey })
          .catch(() => {});
      }
    };

    pc.ontrack = e => {
      const [stream] = e.streams;
      if (!stream) return;
      audioEl.srcObject = stream;
      audioEl.play().catch(() => {
        // Blocked by the autoplay policy. Common on mobile when the tab
        // regained focus without a fresh gesture. Tell the UI so it can
        // prompt, rather than leaving the user in silence.
        onAudioBlocked?.();
      });

      // Watch the *inbound* stream so remote speakers get a speaking
      // ring too. Without this only the local user ever lights up.
      entry.stopSpeakingWatch?.();
      entry.stopSpeakingWatch = watchSpeaking(stream, speaking => {
        const current = peers.get(pubkey);
        if (!current) return;
        current.speaking = speaking;
        emit();
      });

      onRemoteStream?.(pubkey, stream);
      emit();
    };

    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'closed') {
        closePeer(pubkey);
      }
      emit();
    };

    // Publish our mic to this peer if we're a speaker.
    if (selfRole === 'speaker' && localStream) {
      for (const track of localStream.getTracks()) pc.addTrack(track, localStream);
    }

    peers.set(pubkey, entry);
    return entry;
  }

  function closePeer(pubkey: string) {
    const p = peers.get(pubkey);
    if (!p) return;
    try { p.stopSpeakingWatch?.(); } catch { /* already stopped */ }
    try { p.pc.close(); } catch { /* already closed */ }
    try {
      p.audioEl.srcObject = null;
      p.audioEl.remove();
    } catch { /* detached */ }
    peers.delete(pubkey);
    emit();
  }

  /** Re-publish local tracks to every peer after becoming a speaker. */
  async function republishToAll() {
    if (!localStream) return;
    for (const [pubkey, p] of peers) {
      // Drop any stale senders first so we never double-publish.
      for (const sender of p.pc.getSenders()) {
        if (sender.track) {
          try { p.pc.removeTrack(sender); } catch { /* ignore */ }
        }
      }
      for (const track of localStream.getTracks()) {
        try { p.pc.addTrack(track, localStream); } catch { /* ignore */ }
      }
      // Renegotiate as the offerer.
      try {
        const offer = await p.pc.createOffer();
        await p.pc.setLocalDescription(offer);
        await sendSignal(pubkey, {
          type: 'offer', sdp: offer.sdp || '', from: selfPubkey, role: selfRole,
        });
      } catch { /* peer may have left */ }
    }
  }

  return {
    async join(role: RoomRole) {
      selfRole = role;
      if (role === 'speaker') {
        const stream = await ensureMic();
        // Fall back to listening if the mic is unavailable — better than
        // failing the whole join.
        if (!stream) selfRole = 'listener';
        else startLocalSpeakingWatch();
      }
      joined = true;
      emit();
      // Announce ourselves; existing participants will offer to us.
      await sendSignal('', { type: 'join', from: selfPubkey, role: selfRole }).catch(() => {});
    },

    async announce() {
      if (!joined) return;
      await sendSignal('', { type: 'join', from: selfPubkey, role: selfRole })
        .catch(() => {});
    },

    async resumeAudio() {
      for (const [, p] of peers) {
        if (!p.audioEl.srcObject) continue;
        p.audioEl.muted = false;
        try { await p.audioEl.play(); } catch { /* still blocked */ }
      }
    },

    leave() {
      joined = false;
      sendSignal('', { type: 'leave', from: selfPubkey }).catch(() => {});
      for (const pubkey of [...peers.keys()]) closePeer(pubkey);
      releaseMic();
      emit();
    },

    setMuted(muted: boolean) {
      selfMuted = muted;
      localStream?.getAudioTracks().forEach(t => { t.enabled = !muted; });
      if (muted) selfSpeaking = false;
      // Peers cannot detect a disabled track, so tell them.
      broadcastState();
      emit();
    },

    setHandRaised(raised: boolean) {
      selfHandRaised = raised;
      broadcastState();
      emit();
    },

    async setRole(role: RoomRole) {
      if (role === selfRole) return;
      selfRole = role;
      if (role === 'speaker') {
        const stream = await ensureMic();
        if (!stream) { selfRole = 'listener'; emit(); return; }
        startLocalSpeakingWatch();
        await republishToAll();
      } else {
        // Demoted to listener — stop publishing and free the mic.
        for (const [, p] of peers) {
          for (const sender of p.pc.getSenders()) {
            if (sender.track) {
              try { p.pc.removeTrack(sender); } catch { /* ignore */ }
            }
          }
        }
        releaseMic();
      }
      emit();
    },

    getLocalStream() {
      return localStream;
    },

    async handleSignal(payload: SignalPayload) {
      const from = payload.from;
      if (!from || from === selfPubkey) return;

      switch (payload.type) {
        case 'join': {
          // Someone joined. Only speakers need to initiate (they have audio
          // to send); listener↔listener pairs would exchange nothing.
          const peer = getPeer(from, payload.role);
          if (selfRole === 'speaker') {
            try {
              const offer = await peer.pc.createOffer();
              await peer.pc.setLocalDescription(offer);
              await sendSignal(from, {
                type: 'offer', sdp: offer.sdp || '', from: selfPubkey, role: selfRole,
              });
            } catch { /* ignore */ }
          }
          // Tell the newcomer our current mute / hand state, otherwise they
          // would see stale defaults until the next time it changes.
          sendSignal(from, {
            type: 'state',
            from: selfPubkey,
            role: selfRole,
            muted: selfMuted,
            handRaised: selfHandRaised,
          }).catch(() => {});
          emit();
          break;
        }

        case 'state': {
          // Mute / hand-raise are self-reported: a peer may only describe
          // itself, which is exactly what this does (keyed on `from`).
          const peer = peers.get(from);
          if (!peer) break;
          peer.role = payload.role;
          peer.muted = payload.muted;
          peer.handRaised = payload.handRaised;
          if (payload.muted) peer.speaking = false;
          emit();
          break;
        }

        case 'offer': {
          const peer = getPeer(from, payload.role);
          peer.role = payload.role;
          try {
            await peer.pc.setRemoteDescription({ type: 'offer', sdp: payload.sdp });
            const answer = await peer.pc.createAnswer();
            await peer.pc.setLocalDescription(answer);
            await sendSignal(from, {
              type: 'answer', sdp: answer.sdp || '', from: selfPubkey, role: selfRole,
            });
          } catch { /* ignore */ }
          emit();
          break;
        }

        case 'answer': {
          const peer = peers.get(from);
          if (!peer) break;
          peer.role = payload.role;
          try {
            await peer.pc.setRemoteDescription({ type: 'answer', sdp: payload.sdp });
          } catch { /* ignore */ }
          emit();
          break;
        }

        case 'ice': {
          const peer = peers.get(from);
          if (!peer) break;
          try {
            await peer.pc.addIceCandidate(payload.candidate);
          } catch { /* candidate may arrive before the description */ }
          break;
        }

        case 'leave':
          closePeer(from);
          break;
      }
    },

    getParticipants() {
      return snapshot();
    },

    removePeer(pubkey: string) {
      closePeer(pubkey);
    },

    destroy() {
      for (const pubkey of [...peers.keys()]) closePeer(pubkey);
      releaseMic();
      joined = false;
    },
  };
}

// ── Local speaking detection ─────────────────────────────────────

/**
 * Watch a MediaStream's audio level and call back when speech starts or
 * stops. Returns a cleanup function.
 *
 * Uses RMS over the frequency data rather than a raw threshold so it
 * tracks speech rather than clicks and hiss.
 */
export function watchSpeaking(
  stream: MediaStream,
  onChange: (speaking: boolean) => void,
  threshold = 0.015,
): () => void {
  if (stream.getAudioTracks().length === 0) return () => {};

  let ctx: AudioContext | null = null;
  let raf = 0;
  let active = true;
  let last = false;

  try {
    ctx = new AudioContext();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.5;
    source.connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);

    const tick = () => {
      if (!active) return;
      analyser.getByteFrequencyData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += data[i] * data[i];
      const rms = Math.sqrt(sum / data.length) / 255;
      const speaking = rms > threshold;
      if (speaking !== last) {
        last = speaking;
        onChange(speaking);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
  } catch {
    return () => {};
  }

  return () => {
    active = false;
    cancelAnimationFrame(raf);
    try { ctx?.close(); } catch { /* already closed */ }
  };
}
